/**
 * Availability filtering for the panel configurator's option drawers.
 *
 * Pure functions over the cached product payload; nothing here reads Redux or
 * React. Implements the "Availability Filtering" and "Re-resolution" sections
 * of the Product Card Drawers spec (tsi-requirements:
 * common-library/apps/configurator/panel-configurator/product-card-drawers.md).
 */
import { Immutable } from "immer";

import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValue,
    IOptionValues,
    IProduct,
} from "../../models/catalogue";
import { sortProductOptions } from "../../utils/sorting";
import { ICardContentRootProduct } from "./models";

export const LEVEL_CODE: IOptionCode = "option_level";

/**
 * Orders the values a drawer renders. Pill drawers use the option's standard
 * order; card drawers use the editorial order from card content. Required
 * everywhere so rendering and resolution can't fall back to different orders.
 */
export type IDisplayOrder = (
    code: IOptionCode,
    values: readonly IOptionSingleValue[],
) => IOptionSingleValue[];

const standardDisplayOrder: IDisplayOrder = (code, values) =>
    sortProductOptions(code, values);

/**
 * Display order for a root product's drawers: card drawers render in the
 * editorial order from card content (which also covers a card drawer
 * degraded to pills), everything else in the standard order.
 */
export const cardDisplayOrder =
    (cardContent: ICardContentRootProduct | undefined): IDisplayOrder =>
    (code, values) => {
        const standard = standardDisplayOrder(code, values);
        const order = cardContent?.options[code]?.order;
        if (!order) {
            return standard;
        }
        const rank = (value: IOptionSingleValue) => {
            const idx = order.indexOf(value);
            return idx === -1 ? order.length + standard.indexOf(value) : idx;
        };
        return [...standard].sort((a, b) => rank(a) - rank(b));
    };

type Variants = Immutable<IProduct[]>;
type Tuple = readonly IOptionSingleValue[];

const isAvailableToBuy = (variant: Immutable<IProduct>): boolean =>
    variant.availability.is_available_to_buy;

export const variantsOf = (root: Immutable<IProduct>): Variants =>
    root.children.length > 0 ? root.children : [root];

export const valueOf = (
    variant: Immutable<IProduct>,
    code: IOptionCode,
): IOptionValue | undefined => {
    const value = variant.attributes[code]?.value;
    if (typeof value === "string") {
        return value;
    }
    if (Array.isArray(value)) {
        return value.filter(
            (v): v is IOptionSingleValue => typeof v === "string",
        );
    }
    return undefined;
};

export const asTuple = (value: IOptionValue | undefined): Tuple | undefined => {
    if (value === undefined) {
        return undefined;
    }
    return typeof value === "string" ? [value] : value;
};

export const sameValue = (
    a: IOptionValue | undefined,
    b: IOptionValue | undefined,
): boolean => {
    const ta = asTuple(a);
    const tb = asTuple(b);
    // Both absent matches, as in getProductVariantsForOptionCodes.
    if (!ta && !tb) {
        return true;
    }
    if (!ta || !tb || ta.length !== tb.length) {
        return false;
    }
    return ta.every((v, i) => v === tb[i]);
};

/**
 * True when no variant of the root product is available to buy. Filtering is
 * then suspended for that root product and every drawer beneath it (rule 6):
 * all values render and the customer reaches the unavailable message.
 */
export const isFilteringSuspended = (root: Immutable<IProduct>): boolean =>
    !variantsOf(root).some(isAvailableToBuy);

/**
 * Root products the root product selector offers (rule 6): those with an
 * available variant, plus any kept because the customer arrived on them
 * (a restored URL keeps its root product for the session). If no root
 * product is available, every one is offered and filtering is suspended
 * beneath each.
 */
export const offeredRootProducts = <T extends Immutable<IProduct>>(
    roots: readonly T[],
    keptRootIDs: ReadonlySet<T["id"]> = new Set(),
): T[] => {
    if (roots.every(isFilteringSuspended)) {
        return [...roots];
    }
    return roots.filter(
        (root) => !isFilteringSuspended(root) || keptRootIDs.has(root.id),
    );
};

/**
 * Variants matching the selections for every code before `code`, in drawer
 * order. A code missing from `orderedCodes` is treated as coming after every
 * drawer. When filtering is suspended for the root product, availability is
 * ignored ("offered" rather than "available").
 */
export const candidateVariants = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
    code: IOptionCode,
): Variants => {
    const at = orderedCodes.indexOf(code);
    const priorCodes = at === -1 ? orderedCodes : orderedCodes.slice(0, at);
    const suspended = isFilteringSuspended(root);
    return variantsOf(root).filter(
        (variant) =>
            (suspended || isAvailableToBuy(variant)) &&
            priorCodes.every((prior) =>
                sameValue(valueOf(variant, prior), selections[prior]),
            ),
    );
};

/**
 * The values (tuples, for multi-value options) a drawer can offer given the
 * selections in the drawers before it, in display order. Multi-value options
 * are evaluated as tuples, never per selector (rule 5).
 */
export const availableTuples = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
    code: IOptionCode,
    displayOrder: IDisplayOrder,
): Tuple[] => {
    const seen = new Map<string, Tuple>();
    for (const variant of candidateVariants(
        root,
        orderedCodes,
        selections,
        code,
    )) {
        const tuple = asTuple(valueOf(variant, code));
        if (tuple && tuple.length > 0) {
            seen.set(JSON.stringify(tuple), tuple);
        }
    }
    const tuples = [...seen.values()];
    const width = Math.max(0, ...tuples.map((t) => t.length));
    const rank = Array.from({ length: width }, (_, i) => {
        const order = displayOrder(code, [
            ...new Set(tuples.filter((t) => i < t.length).map((t) => t[i])),
        ]);
        return (value: IOptionSingleValue | undefined) => {
            const idx = value === undefined ? -1 : order.indexOf(value);
            return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
        };
    });
    return tuples.sort((a, b) => {
        for (let i = 0; i < width; i++) {
            const diff = rank[i](a[i]) - rank[i](b[i]);
            if (diff !== 0) {
                return diff;
            }
        }
        return 0;
    });
};

/**
 * Values selector `index` of a drawer can offer: every value appearing at
 * that position in an available tuple. For single-value options this is the
 * drawer's value list.
 */
export const availableValues = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
    code: IOptionCode,
    displayOrder: IDisplayOrder,
    index = 0,
): IOptionSingleValue[] => {
    const values = availableTuples(
        root,
        orderedCodes,
        selections,
        code,
        displayOrder,
    )
        .filter((tuple) => index < tuple.length)
        .map((tuple) => tuple[index]);
    return displayOrder(code, [...new Set(values)]);
};

/**
 * The Level Selection Panel's window (rule 4): the selected level and the
 * next available one up, or the next available one down when the selected
 * level is the highest. One column when no other level qualifies.
 */
export const levelWindow = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
    displayOrder: IDisplayOrder,
): IOptionSingleValue[] => {
    const levels = availableValues(
        root,
        orderedCodes,
        selections,
        LEVEL_CODE,
        displayOrder,
    );
    const current = asTuple(selections[LEVEL_CODE])?.[0];
    const idx = current === undefined ? -1 : levels.indexOf(current);
    if (idx === -1) {
        return levels.slice(0, 2);
    }
    if (idx + 1 < levels.length) {
        return [levels[idx], levels[idx + 1]];
    }
    return idx > 0 ? [levels[idx - 1], levels[idx]] : [levels[idx]];
};

/**
 * The variant the selections resolve to: every selected code matches, and
 * it is available to buy unless filtering is suspended for the root product.
 */
const matchingVariant = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
): Immutable<IProduct> | null => {
    const suspended = isFilteringSuspended(root);
    return (
        variantsOf(root).find(
            (variant) =>
                (suspended || isAvailableToBuy(variant)) &&
                orderedCodes.every(
                    (code) =>
                        selections[code] === undefined ||
                        sameValue(valueOf(variant, code), selections[code]),
                ),
        ) ?? null
    );
};

/**
 * The level window with the variant each column resolves to.
 */
export const levelWindowColumns = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
    displayOrder: IDisplayOrder,
): Readonly<{
    level: IOptionSingleValue;
    variant: Immutable<IProduct> | null;
}>[] =>
    levelWindow(root, orderedCodes, selections, displayOrder).map((level) => ({
        level,
        variant: matchingVariant(root, orderedCodes, {
            ...selections,
            [LEVEL_CODE]: level,
        }),
    }));

export type IPinnedSelection = Readonly<{
    code: IOptionCode;
    index: number;
}>;

export type IResolution = Readonly<{
    selections: IOptionValues;
    changed: boolean;
}>;

/**
 * Keep the current value if it is still available, otherwise take the first
 * one in display order (for levels, the lowest available one).
 */
const resolveTuple = (
    tuples: readonly Tuple[],
    current: IOptionValue | undefined,
    pinnedIndex: number | undefined,
): Tuple => {
    const kept = tuples.find((t) => sameValue(t, current));
    if (kept) {
        return kept;
    }
    // The customer just picked one selector of a multi-value option: keep
    // that pick and resolve the other positions around it.
    const currentTuple = asTuple(current);
    if (pinnedIndex !== undefined && currentTuple) {
        const pinnedValue = currentTuple[pinnedIndex];
        const withPin = tuples.find((t) => t[pinnedIndex] === pinnedValue);
        if (withPin) {
            return withPin;
        }
    }
    return tuples[0];
};

/**
 * Re-resolve the selections after an earlier one changed (spec:
 * Re-resolution). Walks the drawers in order: a drawer keeps its value if it
 * is still available, otherwise takes the first available value in the
 * order the drawer renders, then the drawers after it are re-filtered against
 * that. Never a "closest value"; option values have no distance metric.
 *
 * `pinned` is the selection the customer just made: drawers before it are
 * left alone, and the pinned drawer keeps the value at `pinned.index` and
 * re-resolves its other positions around it.
 *
 * Also used on load, so a configuration restored from a URL that names a
 * value filtering removed is repaired before first paint. `changed` tells the
 * caller whether anything was repaired.
 */
export const resolveSelections = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
    options: Readonly<{
        pinned?: IPinnedSelection;
        displayOrder: IDisplayOrder;
    }>,
): IResolution => {
    const { displayOrder } = options;
    const pinnedAt = options.pinned
        ? orderedCodes.indexOf(options.pinned.code)
        : -1;
    const resolved: IOptionValues = { ...selections };
    let changed = false;

    orderedCodes.forEach((code, position) => {
        const current = resolved[code];
        if (position < pinnedAt) {
            return;
        }
        const tuples = availableTuples(
            root,
            orderedCodes,
            resolved,
            code,
            displayOrder,
        );
        if (tuples.length === 0) {
            return;
        }
        const next = resolveTuple(
            tuples,
            current,
            position === pinnedAt ? options.pinned?.index : undefined,
        );
        if (sameValue(next, current)) {
            return;
        }
        resolved[code] =
            Array.isArray(current) || next.length > 1 ? [...next] : next[0];
        changed = true;
    });

    return { selections: resolved, changed };
};
